import type { MinimalDrop } from "@anaconda/core/drop/types"
import { atomizeDrop } from "@anaconda/core/drop/utils"
import { useMolecule } from "bunshi/react"
import { useMemo } from "react"
import { DropFetchMolecule, DropMoleculeReatom } from "./drop-store"
import { DropScope } from "./scope"

export function useFetchedDrop(): MinimalDrop {
  const { dropDataAtom } = useMolecule(DropFetchMolecule)
  // dropDataAtom is suspense-based, use .suspended() to get resolved value
  const drop = dropDataAtom.suspended()
  return drop
}

export function useDrop(drop: MinimalDrop) {
  // Atomize drop for stable references
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const dropAtom = useMemo(() => atomizeDrop(drop, "useDrop"), [drop.id])

  const dropMolecule = useMolecule(DropMoleculeReatom, {
    withScope: [DropScope, dropAtom],
  })

  // Subscribe to effects by referencing them (not calling)
  dropMolecule.tickerEffect
  dropMolecule.mintedDataRefetchEffect
  dropMolecule.eligibilityDataRefetchEffect

  return dropMolecule
}

export function useNextPhaseTransition(drop: MinimalDrop) {
  // Atomize drop for stable references
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const dropAtom = useMemo(
    () => atomizeDrop(drop, "useNextPhaseTransition"),
    [drop.id],
  )

  const dropMolecule = useMolecule(DropMoleculeReatom, {
    withScope: [DropScope, dropAtom],
  })

  const nextTransitionTime = dropMolecule.nextTransitionTimeAtom()
  const now = dropMolecule.nowAtom()

  if (!nextTransitionTime) return null

  const timeUntilTransition = nextTransitionTime - now
  const isTransitionSoon =
    timeUntilTransition <= 5000 && timeUntilTransition > 0

  return {
    nextTransitionTime,
    timeUntilTransition,
    isTransitionSoon,
  }
}

export function useDropFee(drop: MinimalDrop) {
  const dropMolecule = useDrop(drop)
  return dropMolecule.dropFeeAtom()
}

export function useDropDisplayPrice(drop: MinimalDrop) {
  const dropMolecule = useDrop(drop)
  return dropMolecule.dropDisplayPriceAtom()
}
